/**
 * NetWorld - connects the local World to a multiplayer game session.
* Abstract base for replication-mode-specific NetWorlds. Tracks `INetObject`
* implementations, owns the active session (`IGameSession` subtype per subclass),
* and provides shared RPC dispatch plumbing.
 */
import { Actor } from '../actors/Actor.js';
import { NetRole } from '../actors/ActorNetworking.js';
import { PlayerController } from '../actors/PlayerController.js';
import { SceneComponent } from '../components/SceneComponent.js';
import { NET_HEARTBEAT_INTERVAL_SEC, NET_OBJECT_TTL_SEC } from '../const.js';
import { Playable } from '../game/Playable.js';
import { ConsoleCommand } from '../systems/ConsoleManager.js';
import { getGeneratedRpcMetadata } from '../utils/serialization/decorator.js';
import { shortUUID } from '../utils/uuid.js';

import { GameSessionState } from './interface.js';
import { NetMovementPredictor } from './MovementPrediction.js';
import {
  getDeviceId,
  makeNetId,
  makeStaticNetId,
} from './NetIdentity.js';
import { NetRuntime } from './NetRuntime.js';

import type { LagSimulationConfig } from './implementations/LagSimulatingNetworkTransport.js';
import type {
  ClientId,
  ClientState,
  DeviceId,
  IGameSession,
  JoinParams,
  NetId,
  ObjectId
} from './interface.js';
import type { INetObject } from './NetIdentity.js';
import type { IRPCTarget, RPCOptions } from '../actors/ActorNetworking.js';
import type { World } from '../game/World.js';
import type { UIElement } from '../ui/index.js';
import type { NetPropertyMetadata } from '../utils/serialization/decorator.js';

export {
  getDeviceId,
  isStaticNetId,
  makeNetId,
  makeStaticNetId,
  parseNetId,
} from './NetIdentity.js';

export type { INetObject, NetIdStruct } from './NetIdentity.js';

export type RemoteNetObjectRecord = {
  object: INetObject;
  remoteLastUpdatedTime: number | null;
  localLastUpdatedTime: number | null;
};

/**
 * Per-client transport stats surfaced to the stats UI on the server.
 */
type ClientTransportStats = {
  bytesSentPerSecond: number;
  bytesReceivedPerSecond: number;
  totalBytesSent: number;
  totalBytesReceived: number;
  rttMs: number | null;
};

type NetStats = {
  numLocalObjects: number;
  numRemoteObjects: number;
  numLocalRelevantObjects: number;
  numLocalUpdatingObjects: number;
  numRemoteUpdatingObjects: number;
  numClientsInSession: number;

  // Connection-level bandwidth + RTT pulled from the active INetworkConnection.
  // Reflects ALL traffic including replication, RPC, ack, ping, handshake, etc.
  bytesSentPerSecond: number;
  bytesReceivedPerSecond: number;
  totalBytesSent: number;
  totalBytesReceived: number;
  serverRttMs: number | null;

  // Server-only: per-client transport stats keyed by clientId.
  perClient: Record<string, ClientTransportStats>;

  numActorsReplicated: number;
  numIncomingRPCs: number;
  numOutgoingRPCs: number;
}

export interface NetSettings {
  /** Server URL to connect to for multiplayer clients (e.g. `ws://localhost:7777`). */
  serverUrl?: string;
  /** Port range [minPort, maxPort] for dedicated server mode. */
  serverPortRange?: [number, number];
  /** Optional lag simulation applied to client-side server connections. */
  clientLagSimulationConfig?: LagSimulationConfig;
  /** Optional lag simulation applied to server-accepted client connections. */
  serverLagSimulationConfig?: LagSimulationConfig;
}

export interface NetServerStartOptions {
  lagSimulationConfig?: LagSimulationConfig;
}

export interface NetClientConnectOptions {
  serverUrl: string;
  lagSimulationConfig?: LagSimulationConfig;
}

const NetIdKey: string = '__netId__';

/*
* Abstract base class for networking logic between the game and the session layer
* Subclasses implement tick() for different replication modes
*
* @typeParam TSession Concrete session type for this net world (e.g. {@link NetworkGameSession} on clients).
*/
export abstract class NetWorld<TSession extends IGameSession = IGameSession> extends Playable {
  protected gameSession: TSession | null = null;
  public clientId: ClientId = `Client'${shortUUID()}'`;

  // collection of all local actors and components, not all of them are updating
  protected localNetObjects: Map<NetId, INetObject> = new Map<NetId, INetObject>();

  // collection of all replicated actors and their components, not all of them are updating
  protected remoteNetObjects: Map<NetId, RemoteNetObjectRecord> = new Map<NetId, RemoteNetObjectRecord>();

  // objects added to the networld before the game session is set, all considered local
  protected pendingNetObjects: Set<INetObject> = new Set<INetObject>();

  // collection of all historical clients in the session, some of them may have left already
  protected sessionClients: Record<ClientId, ClientState> = {};

  // Heartbeat timer (subclasses may opt in via `startHeartbeatTimer()`).
  protected heartbeatTimer: NodeJS.Timeout | null = null;

  protected statEnabled: boolean = false;
  protected statsUIElement: UIElement | null = null;

  protected actorsDebugEnabled: boolean = false;
  protected actorsDebugUIElement: UIElement | null = null;

  protected netStats: NetStats = {
    numLocalObjects: 0,
    numRemoteObjects: 0,
    numLocalRelevantObjects: 0,
    numLocalUpdatingObjects: 0,
    numRemoteUpdatingObjects: 0,
    numClientsInSession: 0,
    bytesSentPerSecond: 0,
    bytesReceivedPerSecond: 0,
    totalBytesSent: 0,
    totalBytesReceived: 0,
    serverRttMs: null,
    perClient: {},
    numActorsReplicated: 0,
    numIncomingRPCs: 0,
    numOutgoingRPCs: 0,
  };

  public static isVectorNearlyEqual(v1: [number, number, number], v2: [number, number, number], epsilon: number = 0.1): boolean {
    return Math.abs(v1[0] - v2[0]) < epsilon &&
      Math.abs(v1[1] - v2[1]) < epsilon &&
      Math.abs(v1[2] - v2[2]) < epsilon;
  }

  public static isNumberNearlyEqual(v1: number, v2: number, epsilon: number = 0.1): boolean {
    return Math.abs(v1 - v2) < epsilon;
  }

  public static getDeviceId(): DeviceId {
    return getDeviceId();
  }

  protected _world: World | null = null;
  readonly gameId: string;

  constructor(gameId: string) {
    super();
    this.gameId = gameId;
    console.log(`Created NetWorld with gameId: ${this.gameId} and clientId: ${this.clientId}`);
  }

  public get world(): World {
    if (!this._world) {
      throw new Error('[NetWorld] World not set. Call setWorld() first.');
    }
    return this._world;
  }

  public setWorld(world: World | null): void {
    this._world = world;
  }

  /**
   * Set the current map name (server-side only, no-op in base class)
   * Subclasses like ServerAuthorityNetWorld override this to track map changes
   * @param mapName - The name of the map/scene
   */
  public setMapName(mapName: string): void {
    // Base implementation does nothing - subclasses override as needed
  }

  /**
   * Called by `BaseGameLoop.loadMap()` on the **server** after the new scene has finished
   * loading, signalling that any deferred player-joins may now proceed.
   *
   * The base implementation is a no-op; `ServerAuthorityNetWorld` overrides this to flush
   * its `deferredJoinQueue` and set `mapTransitionState = 'ready'`.
   */
  public onServerMapReady(): void {
    // No-op in base class.
  }

  /**
   * Returns the {@link IServerMapCoordinator} for this `NetWorld`, if one exists.
   *
   * `PlayerController` uses this accessor to notify the server when a client has
   * finished loading a map, without relying on an `as any` cast.
   *
   * @returns The coordinator, or `null` for non-server implementations such as
   *   `StandaloneNetWorld` and `ClientNetWorld`.
   */
  public getMapCoordinator(): import('./IServerMapCoordinator.js').IServerMapCoordinator | null {
    return null;
  }

  /**
   * Abstract tick method - subclasses implement different replication strategies
   */
  public abstract tick(deltaTime: number): Promise<void>;

  /**
   * Refresh `netStats` and redraw the stats / debug UI panels.
   *
   * Subclasses must call this every tick (typically at the end of `tick()` and
   * before any early returns) so the network statistics overlay stays current.
   *
   * @param numLocalUpdatingObjects Number of locally-owned net objects that
   *   produced an update payload this tick (used for the "Local Updating
   *   Objects" line in the stats UI).
   */
  protected updateNetStats(numLocalUpdatingObjects: number): void {
    // Pull connection-level transport stats (bandwidth + RTT) from the
    // active session manager. These reflect ALL traffic, not just replication.
    this.refreshTransportStats();

    this.netStats.numLocalObjects = this.localNetObjects.size;
    this.netStats.numRemoteObjects = this.remoteNetObjects.size;
    this.netStats.numLocalUpdatingObjects = numLocalUpdatingObjects;

    let numClients = 0;
    for (const clientId of Object.keys(this.sessionClients)) {
      if (clientId !== this.clientId) {
        numClients++;
      }
    }
    this.netStats.numClientsInSession = numClients;

    if (this.statEnabled) {
      this.updateStatsUI();
    }

    if (this.actorsDebugEnabled) {
      this.updateActorsDebugUI();
    }
  }

  /**
   * Gets the active game session.
   * @returns The game session, or null if none is attached.
   */
  public getGameSession(): TSession | null {
    return this.gameSession;
  }

  /**
   * Start a server on the given port range.
   *
   * Only supported by `ServerAuthorityNetWorld`; other NetWorld types throw.
   * @param portRange - [minPort, maxPort] to try
   * @returns The port that was successfully bound
   */
  public async startServer(_portRange: [number, number], _options: NetServerStartOptions = {}): Promise<number> {
    throw new Error(`[${this.constructor.name}] startServer() is not supported by this NetWorld type.`);
  }

  /**
   * Attach an already-constructed game session to this NetWorld.
   *
   * The session is expected to be ready to use:
   * - Client-side: the caller has already awaited `session.waitUntilReady()`.
   * - Server-side: the caller has already awaited `session.startListening(...)`.
   *
   * If the session reports a server-assigned client ID (e.g. via handshake),
   * this NetWorld's `clientId` is updated to match.
   *
   * Callers outside `NetWorld` should use subclass entry points (e.g. {@link ClientNetWorld.connectToServer})
   * rather than attaching arbitrary session implementations.
   */
  protected async attachGameSession(session: TSession): Promise<TSession> {
    if (this.gameSession) {
      throw new Error('Game session already exists');
    }
    this.gameSession = session;
    console.log(`[NetWorld] Attached game session for game ${session.getDescription().gameId}`);

    const assignedClientId = session.getAssignedClientId();
    if (assignedClientId && assignedClientId !== this.clientId) {
      console.log(`[NetWorld] Updating clientId from ${this.clientId} to server-assigned ${assignedClientId}`);
      this.clientId = assignedClientId;
    }

    await this.onGameSessionSet();
    return session;
  }

  /**
   * Adds a net object to the world
   * This can either be called by a locally spwaned object or one replicated from other clients
   * @param netObject - The net object
   */
  public addNetObject(netObject: INetObject): void {
    if (!this.gameSession) {
      this.pendingNetObjects.add(netObject);
      return;
    }

    // Determine NetID and whether this is a local or remote object
    // This logic differs between peer-to-peer and server-authority modes
    const { netId, isLocal } = this.determineNetIdAndOwnership(netObject);

    if (isLocal) {
      if (this.localNetObjects.has(netId)) {
        throw new Error(`Local net object ${netId} already exists`);
      }
      this.localNetObjects.set(netId, netObject);
    } else {
      // For remote objects, skip if already registered (can happen when components
      // call beginPlay after their parent actor was already replicated)
      if (this.remoteNetObjects.has(netId)) {
        return;
      }
      this.remoteNetObjects.set(netId, {
        object: netObject,
        remoteLastUpdatedTime: null,
        localLastUpdatedTime: Date.now()
      });
    }
    this.setNetId(netObject, netId);
  }

  /**
   * Determine the NetID and ownership for a net object
   * This method can be overridden by subclasses to implement different logic
   * for client vs server-authority modes
   */
  protected determineNetIdAndOwnership(netObject: INetObject): { netId: NetId; isLocal: boolean } {
    // Default implementation (used by `ClientNetWorld`, `StandaloneNetWorld`, and other subclasses that do not override):
    // Static objects use deterministic NetIDs, dynamic objects use standard NetIDs
    let netId: NetId;
    let isLocal: boolean;

    if (netObject instanceof Actor && netObject.isStaticNetObject()) {
      // Static objects use deterministic NetIDs based on their UUID
      // Both server and client generate the same NetID for matching
      const className = netObject.constructor.name;
      netId = makeStaticNetId(netObject.uuid, className);
      // Static objects are always "local" in the sense that they exist on both sides
      // and don't need to be replicated as new objects
      isLocal = true;
    } else {
      // Dynamic objects use the standard NetID format
      const deviceId = netObject.getNetDeviceId();
      const clientId = netObject.getNetClientId();
      const objectId = netObject.getNetObjectId();
      isLocal = clientId === this.clientId;
      netId = makeNetId({ deviceId, clientId, objectId });
    }

    return { netId, isLocal };
  }

  /**
   * Find a local static object by its UUID
   * Used for matching server static objects with client-side copies
   * @param uuid - The actor's UUID from the world file
   * @returns The local actor if found, or null
   */
  public getLocalActorByUUID(uuid: string): Actor | null {
    return this.world.getActorByUuid(uuid) ?? null;
  }

  /**
   * Find a static net object by its deterministic NetID
   * @param uuid - The actor's UUID
   * @param className - The actor's class name
   * @returns The net object if found, or null
   */
  public getStaticNetObject(uuid: string, className: string): INetObject | null {
    const netId = makeStaticNetId(uuid, className);
    return this.localNetObjects.get(netId) ?? this.remoteNetObjects.get(netId)?.object ?? null;
  }

  /**
   * Removes a net object from the world
   * This can either be called by a locally spawned object or one replicated from other clients
   * @param netObject - The net object
   */
  public removeNetObject(netObject: INetObject): void {
    if (this.pendingNetObjects.has(netObject)) {
      this.pendingNetObjects.delete(netObject);
      return;
    }
    if (!this.gameSession) {
      return;
    }
    const netId = this.getNetId(netObject);
    this.localNetObjects.delete(netId);
    this.remoteNetObjects.delete(netId);
  }

  /**
   * Clears all net object tracking data
   * Called during disconnect to reset the NetWorld for reconnection
   */
  public clearNetObjects(): void {
    console.log('[NetWorld] Clearing all net objects');
    this.localNetObjects.clear();
    this.remoteNetObjects.clear();
    this.pendingNetObjects.clear();
  }

  //====================================================================================================

  /**
   * Called once a game session has been created or joined.
   * Subclasses implement this to wire up session event handlers, send the initial
   * heartbeat, register console commands, and start any replication-specific timers.
   * The base implementation is a no-op so `StandaloneNetWorld` (which never sets a
   * session) doesn't need an override.
   */
  protected async onGameSessionSet(): Promise<void> {
    // Subclasses override as needed.
  }

  /**
   * Re-registers any net objects that were `addNetObject()`d before the game session
   * existed. Subclasses call this from `onGameSessionSet()` once `gameSession` is set.
   */
  protected flushPendingNetObjects(): void {
    for (const netObject of this.pendingNetObjects) {
      this.addNetObject(netObject);
    }
    this.pendingNetObjects.clear();
  }

  /**
   * Start the periodic heartbeat timer. Idempotent.
   * Subclasses call this from `onGameSessionSet()` when they want periodic
   * `clientHeartBeat()` messages (e.g. clients and peers).
   */
  protected startHeartbeatTimer(): void {
    if (this.heartbeatTimer) {
      return;
    }
    console.log(`Starting heartbeat timer with interval: ${NET_HEARTBEAT_INTERVAL_SEC} sec`);
    this.heartbeatTimer = setInterval(() => {
      this.sendHeartbeat();
    }, NET_HEARTBEAT_INTERVAL_SEC * 1000);
  }

  /**
   * Called when a player joins the session. Default implementation just logs.
   * Subclasses (ServerAuthorityNetWorld) override to drive server-side join logic.
   */
  protected async onSessionPlayerJoined(clientId: string, _joinParams: JoinParams): Promise<void> {
    console.log(`[NetWorld] Player joined: ${clientId}`);
  }

  /**
   * Called when a player leaves the session. Default implementation just logs.
   * Subclasses (ServerAuthorityNetWorld) override to drive server-side cleanup.
   */
  protected onSessionPlayerLeft(clientId: string): void {
    console.log(`[NetWorld] Player left: ${clientId}`);
  }

  /**
   * Called when session state changes
   */
  protected onSessionStateChanged(oldState: GameSessionState, newState: GameSessionState): void {
    console.log(`[NetWorld] Session state changed: ${oldState} → ${newState}`);
  }

  /**
   * Pull connection-level bandwidth + RTT stats from the active game
   * session into `netStats`. Must be called every tick to keep the UI fresh.
   *
   * - On the client: aggregates the single connection to the server.
   * - On the server: sums all connected clients into the totals AND fills
   *   `netStats.perClient` with per-client snapshots.
   */
  private refreshTransportStats(): void {
    const session = this.gameSession;
    if (!session) {
      return;
    }

    if (NetRuntime.isDedicatedServer()) {
      const perClient: Record<string, ClientTransportStats> = {};
      let bytesSentPerSecond = 0;
      let bytesReceivedPerSecond = 0;
      let totalBytesSent = 0;
      let totalBytesReceived = 0;
      const stats = session.getClientStats();
      for (const [clientId, snapshot] of Object.entries(stats)) {
        perClient[clientId] = {
          bytesSentPerSecond: snapshot.bytesSentPerSecond,
          bytesReceivedPerSecond: snapshot.bytesReceivedPerSecond,
          totalBytesSent: snapshot.bytesSent,
          totalBytesReceived: snapshot.bytesReceived,
          rttMs: snapshot.rttMs
        };
        bytesSentPerSecond += snapshot.bytesSentPerSecond;
        bytesReceivedPerSecond += snapshot.bytesReceivedPerSecond;
        totalBytesSent += snapshot.bytesSent;
        totalBytesReceived += snapshot.bytesReceived;
      }
      this.netStats.perClient = perClient;
      this.netStats.bytesSentPerSecond = bytesSentPerSecond;
      this.netStats.bytesReceivedPerSecond = bytesReceivedPerSecond;
      this.netStats.totalBytesSent = totalBytesSent;
      this.netStats.totalBytesReceived = totalBytesReceived;
      // Server doesn't have a single "RTT to itself" - leave null.
      this.netStats.serverRttMs = null;
    } else {
      const snapshot = session.getConnectionStats();
      if (snapshot) {
        this.netStats.bytesSentPerSecond = snapshot.bytesSentPerSecond;
        this.netStats.bytesReceivedPerSecond = snapshot.bytesReceivedPerSecond;
        this.netStats.totalBytesSent = snapshot.bytesSent;
        this.netStats.totalBytesReceived = snapshot.bytesReceived;
        this.netStats.serverRttMs = snapshot.rttMs;
      }
      this.netStats.perClient = {};
    }
  }

  /**
   * Send a heartbeat to the active session and refresh the cached client list.
   * Subclasses may override for transport-specific behaviour.
   */
  protected async sendHeartbeat(): Promise<void> {
    if (!this.gameSession) {
      return;
    }

    try {
      const isHealthy = await this.gameSession.clientHeartBeat(this.clientId);
      if (!isHealthy) {
        console.warn(`Heartbeat failed for client ${this.clientId}`);
      }
    } catch (error) {
      console.error('Error sending heartbeat:', error);
    }

    try {
      this.sessionClients = await this.gameSession.getClients();
    } catch (error) {
      console.error('Error getting clients:', error);
    }
  }

  /**
   * Get a snapshot of the current network statistics, including bandwidth
   * (transport in/out) and latency (RTT). On the server, `perClient` is
   * populated with one entry per connected client; on the client it is
   * empty and `serverRttMs` reflects the round trip to the server.
   */
  public getNetStats(): Readonly<NetStats> {
    return this.netStats;
  }

  @ConsoleCommand('netstats', 'Print network bandwidth + latency stats to console')
  private printNetStats(): boolean {
    this.refreshTransportStats();
    const fmt = (bytes: number): string => `${(bytes / 1024).toFixed(2)} KB/s`;
    console.log('[NetWorld]', {
      transportIn: fmt(this.netStats.bytesReceivedPerSecond),
      transportOut: fmt(this.netStats.bytesSentPerSecond),
      totalIn: this.netStats.totalBytesReceived,
      totalOut: this.netStats.totalBytesSent,
      serverRttMs: this.netStats.serverRttMs,
      clients: this.netStats.numClientsInSession
    });
    if (Object.keys(this.netStats.perClient).length > 0) {
      console.table(this.netStats.perClient);
    }
    return true;
  }

  @ConsoleCommand('dumpnetobjects', 'Dump all net objects to console')
  private dumpNetObjects(): boolean {
    const localObjects = Array.from(this.localNetObjects.keys());
    const remoteObjects = Array.from(this.remoteNetObjects.keys());
    console.log(JSON.stringify({ localObjects, remoteObjects }, null, 2));
    return true;
  }

  protected setNetId(netObject: INetObject, netId: NetId, allowOverwrite: boolean = false): void {
    const obj = netObject as any;
    const existingId = obj[NetIdKey];
    if (existingId) {
      if (existingId === netId) {
        return; // Already has the same id
      }
      if (!allowOverwrite) {
        throw new Error(`Net object ${netObject} already has a net id: ${existingId}`);
      }
      // Remove from old tracking
      this.localNetObjects.delete(existingId);
      this.remoteNetObjects.delete(existingId);
    }
    obj[NetIdKey] = netId;
  }

  protected getNetId(netObject: INetObject): NetId {
    const obj = netObject as any;
    const id = obj[NetIdKey];
    if (!id) {
      throw new Error(`Net object ${netObject} does not have a net id`);
    }
    return id;
  }

  @ConsoleCommand('togglemovementprediction', 'Toggle movement prediction (usage: togglemovementprediction <true | false>)')
  private toggleMovementPrediction(flag: string): string {
    NetMovementPredictor.PredictionEnabled = (flag === 'true' || flag === '1');
    return `Movement prediction ${NetMovementPredictor.PredictionEnabled ? 'enabled' : 'disabled'}`;
  }

  @ConsoleCommand('setmovementlerpalpha', 'Set movement prediction lerp alpha (usage: setmovementlerpalpha <value>)')
  private setMovementLerpAlpha(alpha: string): string {
    const alphaValue = parseFloat(alpha);
    if (isNaN(alphaValue)) {
      return `Invalid alpha value: ${alpha}`;
    }
    NetMovementPredictor.LerpAlpha = alphaValue;
    return `Movement prediction lerp alpha set to ${alphaValue}`;
  }

  @ConsoleCommand('stat', 'Toggle network statistics display (usage: stat net)')
  private stat(name: string): string | null {
    if (name.toLocaleLowerCase() === 'net') {
      this.statEnabled = !this.statEnabled;
      if (this.statEnabled) {
        this.createStatsUI();
      } else {
        this.destroyStatsUI();
      }
      return `Net stat ${this.statEnabled ? 'enabled' : 'disabled'}`;
    }
    return null;
  }

  @ConsoleCommand('netactors', 'Toggle network actors debug panel')
  private netactors(): string {
    this.actorsDebugEnabled = !this.actorsDebugEnabled;
    if (this.actorsDebugEnabled) {
      this.createActorsDebugUI();
    } else {
      this.destroyActorsDebugUI();
    }
    return `Network actors debug ${this.actorsDebugEnabled ? 'enabled' : 'disabled'}`;
  }

  private createStatsUI(): void {
    if (this.statsUIElement) {
      return; // Already created
    }

    this.statsUIElement = this.world.uiManager.createElement('net-stats', {
      className: 'net-stats-container',
      style: {
        position: 'absolute',
        top: '100px',
        left: '10px',
        padding: '12px',
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        color: '#00ff00',
        fontFamily: 'monospace',
        fontSize: '16px',
        borderRadius: '4px',
        border: '1px solid #444',
        zIndex: '1000',
        minWidth: '200px',
        lineHeight: '1.4'
      }
    });

    // Set initial content
    this.updateStatsUI();
  }

  private destroyStatsUI(): void {
    if (this.statsUIElement) {
      this.statsUIElement.remove();
      this.statsUIElement = null;
    }
  }

  private updateStatsUI(): void {
    if (!this.statsUIElement) {
      return;
    }

    const formatKbps = (bytes: number): string => `${(bytes * 8 / 1024).toFixed(0)} Kbps`;
    const formatBytes = (bytes: number): string => {
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    };
    const formatRtt = (ms: number | null): string => ms === null ? '—' : `${ms.toFixed(1)} ms`;

    let perClientHTML = '';
    if (NetRuntime.isDedicatedServer()) {
      const entries = Object.entries(this.netStats.perClient);
      if (entries.length > 0) {
        perClientHTML = `
          <div style="margin-top: 8px; padding-top: 4px; border-top: 1px solid #444; font-weight: bold; color: #ffffff;">
            Per-Client Transport
          </div>`;
        for (const [clientId, stats] of entries) {
          perClientHTML += `
            <div style="margin-top: 4px; padding-left: 8px; border-left: 2px solid #4ecdc4;">
              <div style="color: #4ecdc4; font-size: 11px;">${clientId}</div>
              <div style="font-size: 11px;">
                <span style="color: #ffaa00;">RTT:</span> ${formatRtt(stats.rttMs)} &nbsp;
                <span style="color: #ffaa00;">In:</span> ${formatKbps(stats.bytesReceivedPerSecond)} &nbsp;
                <span style="color: #ffaa00;">Out:</span> ${formatKbps(stats.bytesSentPerSecond)}
              </div>
              <div style="font-size: 11px; color: #888;">
                Total: ↓ ${formatBytes(stats.totalBytesReceived)} / ↑ ${formatBytes(stats.totalBytesSent)}
              </div>
            </div>`;
        }
      }
    }

    const html = `
      <div style="font-weight: bold; color: #ffffff; margin-bottom: 8px; border-bottom: 1px solid #444; padding-bottom: 4px;">
        📊 Network Statistics
      </div>
      <div style="margin-bottom: 4px;">
        <span style="color: #ffaa00;">Local Objects:</span> ${this.netStats.numLocalObjects}
      </div>
      <div style="margin-bottom: 4px;">
        <span style="color: #ffaa00;">Remote Objects:</span> ${this.netStats.numRemoteObjects}
      </div>
      <div style="margin-bottom: 4px;">
        <span style="color: #ffaa00;">Local Relevant Objects:</span> ${this.netStats.numLocalRelevantObjects}
      </div>
      <div style="margin-bottom: 4px;">
        <span style="color: #ffaa00;">Local Updating Objects:</span> ${this.netStats.numLocalUpdatingObjects}
      </div>
      <div style="margin-bottom: 4px;">
        <span style="color: #ffaa00;">Remote Updating Objects:</span> ${this.netStats.numRemoteUpdatingObjects}
      </div>
      <div style="margin-bottom: 4px;">
        <span style="color: #ffaa00;">Clients in Session:</span> ${this.netStats.numClientsInSession}
      </div>
      <div style="margin-bottom: 4px;">
        <span style="color: #ffaa00;">Actors Replicated:</span> ${this.netStats.numActorsReplicated}
      </div>
      <div style="margin-bottom: 4px;">
        <span style="color: #ffaa00;">Transport:</span>
        ↓ ${formatKbps(this.netStats.bytesReceivedPerSecond)} / ↑ ${formatKbps(this.netStats.bytesSentPerSecond)}
      </div>
      <div style="margin-bottom: 4px;">
        <span style="color: #ffaa00;">Transport Total:</span>
        ↓ ${formatBytes(this.netStats.totalBytesReceived)} / ↑ ${formatBytes(this.netStats.totalBytesSent)}
      </div>
      <div style="margin-bottom: 4px;">
        <span style="color: #ffaa00;">Incoming RPCs:</span> ${this.netStats.numIncomingRPCs}
      </div>
      <div style="margin-bottom: 4px;">
        <span style="color: #ffaa00;">Outgoing RPCs:</span> ${this.netStats.numOutgoingRPCs}
      </div>
      <div style="margin-bottom: 4px;">
        <span style="color: #ffaa00;">Server RTT:</span> ${formatRtt(this.netStats.serverRttMs)}
      </div>
      <div style="margin-bottom: 4px;">
        <span style="color: #ffaa00;">Movement Prediction Enabled:</span> ${NetMovementPredictor.PredictionEnabled}
      </div>
      <div style="margin-bottom: 4px;">
        <span style="color: #ffaa00;">Movement Lerp Alpha:</span> ${NetMovementPredictor.LerpAlpha}
      </div>
      ${perClientHTML}
      <div style="margin-top: 8px; font-size: 16px; color: #888;">
        Session: ${this.gameSession?.getDescription().sessionName ?? '—'}<br/>
        Device ID: ${getDeviceId()}<br/>
        Game ID: ${this.gameId}<br/>
        Client ID: ${this.clientId}<br/>
      </div>
    `;

    this.statsUIElement.setHTML(html);
  }

  private createActorsDebugUI(): void {
    if (this.actorsDebugUIElement) {
      return; // Already created
    }

    this.actorsDebugUIElement = this.world.uiManager.createElement('net-actors-debug', {
      className: 'net-actors-debug-container',
      style: {
        position: 'absolute',
        top: '100px',
        right: '10px',
        padding: '12px',
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        color: '#00ff00',
        fontFamily: 'monospace',
        fontSize: '12px',
        borderRadius: '4px',
        border: '1px solid #444',
        zIndex: '1000',
        minWidth: '400px',
        maxWidth: '600px',
        maxHeight: '80vh',
        overflowY: 'auto',
        lineHeight: '1.4'
      }
    });

    // Set initial content
    this.updateActorsDebugUI();
  }

  private destroyActorsDebugUI(): void {
    if (this.actorsDebugUIElement) {
      this.actorsDebugUIElement.remove();
      this.actorsDebugUIElement = null;
    }
  }

  private updateActorsDebugUI(): void {
    if (!this.actorsDebugUIElement) {
      return;
    }

    // Collect all network actors
    const networkActors: Array<{ actor: Actor; isLocal: boolean; netId: NetId }> = [];

    // Add local actors
    for (const [netId, netObject] of this.localNetObjects) {
      if (netObject instanceof Actor) {
        networkActors.push({ actor: netObject, isLocal: true, netId });
      }
    }

    // Add remote actors
    for (const [netId, record] of this.remoteNetObjects) {
      if (record.object instanceof Actor) {
        networkActors.push({ actor: record.object, isLocal: false, netId });
      }
    }

    // Sort by class name, then by local/remote
    networkActors.sort((a, b) => {
      const classCompare = a.actor.constructor.name.localeCompare(b.actor.constructor.name);
      if (classCompare !== 0) return classCompare;
      return a.isLocal === b.isLocal ? 0 : a.isLocal ? -1 : 1;
    });

    // Build HTML
    let actorsHTML = '';
    for (const { actor, isLocal, netId } of networkActors) {
      const localRole = actor.netLocalRole;
      const remoteRole = actor.netRemoteRole;
      const owningClientId = actor.netOwningClientId;
      const isRelevant = actor.isNetRelevantFor();

      // Color coding based on role
      let roleColor = '#888';
      if (localRole === NetRole.Authority) roleColor = '#ff6b6b'; // Red for authority
      else if (localRole === NetRole.AutonomousProxy) roleColor = '#4ecdc4'; // Cyan for autonomous
      else if (localRole === NetRole.SimulatedProxy) roleColor = '#95e1d3'; // Light cyan for simulated

      const locationIcon = isLocal ? '📍' : '🌐';
      const relevantIcon = isRelevant ? '✓' : '✗';

      actorsHTML += `
        <div style="margin-bottom: 8px; padding: 8px; background: rgba(255,255,255,0.05); border-left: 3px solid ${roleColor}; border-radius: 2px;">
          <div style="font-weight: bold; color: ${roleColor}; margin-bottom: 4px;">
            ${locationIcon} ${actor.constructor.name}
          </div>
          <div style="font-size: 11px; color: #aaa; margin-bottom: 2px;">
            NetID: ${netId.substring(0, 40)}${netId.length > 40 ? '...' : ''}
          </div>
          <div style="display: flex; gap: 12px; font-size: 11px;">
            <span style="color: #ffaa00;">Local:</span> <span style="color: ${roleColor};">${NetRole[localRole]}</span>
            <span style="color: #ffaa00;">Remote:</span> <span style="color: #888;">${NetRole[remoteRole]}</span>
          </div>
          <div style="display: flex; gap: 12px; font-size: 11px; margin-top: 2px;">
            <span style="color: #ffaa00;">Owner:</span> <span style="color: #888;">${owningClientId ?? 'None'}</span>
            <span style="color: #ffaa00;">Relevant:</span> <span style="color: ${isRelevant ? '#4caf50' : '#888'};">${relevantIcon}</span>
          </div>
        </div>
      `;
    }

    const html = `
      <div style="font-weight: bold; color: #ffffff; margin-bottom: 8px; border-bottom: 1px solid #444; padding-bottom: 4px;">
        🎮 Network Actors (${networkActors.length})
      </div>
      <div style="margin-bottom: 8px; font-size: 11px; color: #888;">
        <span style="color: #ff6b6b;">■</span> Authority
        <span style="color: #4ecdc4; margin-left: 8px;">■</span> Autonomous
        <span style="color: #95e1d3; margin-left: 8px;">■</span> Simulated
        <span style="color: #888; margin-left: 8px;">■</span> None
      </div>
      ${actorsHTML.length > 0 ? actorsHTML : '<div style="color: #888;">No network actors</div>'}
    `;

    this.actorsDebugUIElement.setHTML(html);
  }

  protected override doBeginPlay() {
    this.world.consoleManager.addCommandHandler(this);
  }

  protected override doEndPlay() {
    // Destroy the session (closes any open connection / listening socket
    // and prevents the client from attempting to reconnect).
    this.gameSession?.destroy();

    this.gameSession = null;
    this.localNetObjects.clear();
    this.remoteNetObjects.clear();
    this.pendingNetObjects.clear();
    this.sessionClients = {};

    this.destroyStatsUI();
    this.destroyActorsDebugUI();
    this.world.consoleManager.removeCommandHandler(this);

    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  //====================================================================================================
  // RPC System
  //====================================================================================================

  /**
   * Return the generator-produced `NetPropertyMetadata[]` for an RPC method.
   *
   * The metadata is always expected to be present — it is emitted into `engine-data.ts`
   * by the property metadata generator. Missing metadata is a configuration error
   * (generator not run, or the method was not picked up).
   */
  protected getRpcParamsForMethod(ctor: Function, methodName: string): NetPropertyMetadata[] | null {
    const generated = getGeneratedRpcMetadata(ctor, methodName);
    if (!generated) {
      console.error(
        `[NetWorld] No RPC param metadata for "${methodName}" on ${(ctor as { name?: string }).name ?? '?'}. ` +
        'Ensure the property metadata generator has run and engine-data.ts is up to date.',
      );
    }
    return generated;
  }

  /**
   * Call a method on the owning client only.
   * Only works if the actor has Authority role (server-side).
   * If actor has no owner, the RPC is not sent — use {@link multicastRPC} for non-owned actors.
   *
   * @param target The actor to call the RPC on
   * @param methodName Name of the method to call on owning client
   * @param args Arguments to pass to the method
   * @param options RPC options (reliability)
   */
  public clientRPC(
    target: IRPCTarget,
    methodName: string,
    args: any[] = [],
    options: RPCOptions = {}
  ): void {
    if (!target.hasAuthority()) {
      console.warn(`[NetWorld] clientRPC: Actor ${target.name} does not have authority`);
      return;
    }

    // ClientRPC only sends to owning client
    const owningClientId = target.netOwningClientId;
    if (!owningClientId) {
      console.warn(`[NetWorld] clientRPC: Actor ${target.name} has no owning client. Use multicastRPC() for non-owned actors.`);
      return;
    }

    this.sendClientRPC(
      target.getNetObjectId(),
      methodName,
      args,
      options.reliable ?? true,
      [owningClientId],
      target.getNetSubId?.() ?? undefined
    );
  }

  /**
   * Send a method call to all connected clients (server → all clients).
   * Only works if the actor has Authority role (server-side).
   * Unlike {@link clientRPC}, this does not require the actor to have an owner.
   *
   * @param target The actor to call the RPC on
   * @param methodName Name of the method to call on all clients
   * @param args Arguments to pass to the method
   * @param options RPC options (reliability)
   */
  public multicastRPC(
    target: IRPCTarget,
    methodName: string,
    args: any[] = [],
    options: RPCOptions = {}
  ): void {
    if (!target.hasAuthority()) {
      console.warn(`[NetWorld] multicastRPC: Actor ${target.name} does not have authority`);
      return;
    }

    this.sendClientRPC(
      target.getNetObjectId(),
      methodName,
      args,
      options.reliable ?? true,
      null, // null = broadcast to all clients
      target.getNetSubId?.() ?? undefined
    );
  }

  /**
   * Call a method on the server
   * Only works if the actor is an AutonomousProxy owned by local client
   *
   * @param target The actor to call the RPC on
   * @param methodName Name of the method to call on server
   * @param args Arguments to pass to the method
   * @param options RPC options (reliability)
   */
  public serverRPC(
    target: IRPCTarget,
    methodName: string,
    args: any[] = [],
    options: RPCOptions = {}
  ): void {
    if (!target.isLocalAutonomousProxy()) {
      console.warn(`[NetWorld] serverRPC: Actor ${target.name} is not an autonomous proxy`);
      return;
    }

    if (!target.isOwnedByLocalClient()) {
      console.warn(`[NetWorld] serverRPC: Actor ${target.name} is not owned by local client`);
      return;
    }

    this.sendServerRPC(
      target.getNetObjectId(),
      methodName,
      args,
      options.reliable ?? true,
      target.getNetSubId?.() ?? undefined
    );
  }

  /**
   * Send RPC from server to clients.
   * Only works on server (NetRuntime.isDedicatedServer()).
   *
   * @param objectId      The NetObjectId of the actor to call the method on.
   * @param methodName    The name of the method to call.
   * @param args          Decoded argument values in declaration order.
   * @param reliable      Whether to use a reliable ordered channel.
   * @param targetClients Which clients to send to (null = all clients).
   * @param netSubId      Optional component sub-identifier for component RPC routing.
   */
  public sendClientRPC(
    _objectId: ObjectId,
    _methodName: string,
    _args: unknown[],
    _reliable: boolean,
    _targetClients: ClientId[] | null,
    _netSubId?: string
  ): void {
    console.warn(`[${this.constructor.name}] sendClientRPC is not supported by this NetWorld type.`);
  }

  /**
   * Send RPC from client to server.
   * Only works on client (NetRuntime.isClient()).
   *
   * @param objectId   The NetObjectId of the actor to call the method on.
   * @param methodName The name of the method to call.
   * @param args       Decoded argument values in declaration order.
   * @param reliable   Whether to use a reliable ordered channel.
   * @param netSubId   Optional component sub-identifier for component RPC routing.
   */
  public sendServerRPC(
    _objectId: ObjectId,
    _methodName: string,
    _args: unknown[],
    _reliable: boolean,
    _netSubId?: string
  ): void {
    console.warn(`[${this.constructor.name}] sendServerRPC is not supported by this NetWorld type.`);
  }

  public async waitForLocalPlayerController(): Promise<PlayerController | null> {
    return null;
  }

  /**
   * Dispatch a decoded RPC call to the target actor or component.
   *
   * This is the codec-agnostic dispatch layer: it expects already-decoded `args`
   * and handles routing, security validation, and method invocation.
   * Binary decoding lives in the concrete subclasses ({@link ServerAuthorityNetWorld},
   * {@link ClientNetWorld}) which subscribe to `session.onRPCReceived`.
   *
   * @param actor    The resolved target actor.
   * @param methodName  Name of the RPC method to call.
   * @param args     Decoded argument values (already deserialized from binary).
   * @param netSubId Optional component UUID for component-scoped RPCs.
   * @param senderId The client ID of the sender (server-side only, for security checks).
   */
  protected dispatchRPCCall(
    actor: Actor,
    methodName: string,
    args: unknown[],
    netSubId?: string,
    senderId?: ClientId
  ): void {
    // Security: validate ownership for client→server RPCs
    if (NetRuntime.isDedicatedServer()) {
      const owningClientId = actor.netOwningClientId;
      if (owningClientId && owningClientId !== senderId) {
        console.warn(
          `[NetWorld] RPC security violation: Client ${senderId ?? '<unknown>'} tried to call RPC ` +
          `"${methodName}" on actor owned by ${owningClientId}`,
        );
        return;
      }
    }

    if (netSubId) {
      // Route to a component by uuid
      const component = actor.getComponents(SceneComponent).find(c => c.uuid === netSubId);
      if (!component) {
        console.warn(`[NetWorld] RPC component "${netSubId}" not found on actor ${actor.getNetObjectId()}`);
        return;
      }
      if (!component.isValidRPCMethod(methodName)) {
        console.warn(`[NetWorld] Method "${methodName}" is not a valid RPC on component "${netSubId}"`);
        return;
      }
      const method = (component as any)[methodName];
      if (typeof method === 'function') {
        try {
          method.apply(component, args);
          this.netStats.numIncomingRPCs++;
        } catch (error) {
          console.error(`[NetWorld] Error calling RPC "${methodName}" on component "${netSubId}":`, error);
        }
      } else {
        console.warn(`[NetWorld] RPC method "${methodName}" not found on component "${netSubId}"`);
      }
      return;
    }

    // Validate that the method is a registered RPC on the actor
    if (!actor.isValidRPCMethod(methodName)) {
      console.warn(`[NetWorld] Method "${methodName}" is not a valid RPC on actor ${actor.getNetObjectId()}`);
      return;
    }

    // Call the method on the actor
    const method = (actor as any)[methodName];
    if (typeof method === 'function') {
      try {
        method.apply(actor, args);
        this.netStats.numIncomingRPCs++;
      } catch (error) {
        console.error(`[NetWorld] Error calling RPC "${methodName}" on actor ${actor.getNetObjectId()}:`, error);
      }
    } else {
      console.warn(`[NetWorld] RPC method "${methodName}" not found on actor ${actor.getNetObjectId()}`);
    }
  }

  /**
   * Get local actor by ObjectId (ClassName'UUID')
   * @param objectId The object ID
   * @returns The actor or null if not found
   */
  protected getLocalActorByObjectId(objectId: ObjectId): Actor | null {
    // ObjectId format: "ClassName'UUID'"
    const uuidMatch = objectId.match(/'([^']+)'/);
    if (!uuidMatch) {
      return null;
    }
    const uuid = uuidMatch[1];
    return this.world.getActorByUuid(uuid);
  }

  //====================================================================================================

  protected isClientInactive(clientId: ClientId): boolean {
    const client = this.sessionClients[clientId];
    if (!client) {
      return false;
    }
    return Date.now() - client.lastHeartBeatTime > NET_OBJECT_TTL_SEC * 1000;
  }
}
